<template>
  <v-card style="overflow: hidden;">
    <v-progress-linear indeterminate color="blue" class="my-0" v-if="loading" />
    <v-alert outline class="py-1 ma-0" color="warning" icon="assessment" value="true" v-if="hasError">
      <div class="primary--text">
        {{ errorMessage || 'There\'s no graph data available.' }}
      </div>
    </v-alert>
    <div v-else :id="id" />
  </v-card>
</template>

<script>
  import Highcharts from 'highcharts'
  import DataCharts from '@/components/mixins/DataCharts'
  import axios from 'axios'
  import moment from 'moment'

  const HISTORIC_DISCHARGE = 'Historic Discharge'

  const GRAPH_TITLE = 'Past 2 weeks of data'

  const relevantDatasets = {
    'Hydrology': [
      ['Discharge (m³/s)', 'Water Level', 'Discharge (m3/s)'],
      ['Precipitation', 'Rainfall'],
      ['Air Temp'],
    ],
    'Climate': [
      ['Precipitation'],
      ['Air Temp'],
      ['Solar Radiation'],
    ],
    'Water Quality': [
      ['Turbidity'],
      ['Sp Cond'],
      ['O2 (Dis)'],
      ['Water Temp'],
    ],
  }

  export default {
    mixins: [DataCharts],
    props: {
      station: Object,
      datasets: Array,
      id: String,
      selectedTimeSeries: Object,
    },
    data() {
      return {
        loading: true,
        hasError: false,
        errorMessage: null,
      }
    },
    computed: {
      sortedDatasets() {
        if (!this.datasets) return []

        return [...this.datasets].sort((d1, d2) => {
          return new Date(d2.LastRecordDate) - new Date(d1.LastRecordDate)
        })
      },
      name() {
        return this.station && this.station.LocationIdentifier
      },
      locationType() {
        return this.station && this.station.LocationType
      },
    },
    watch: {
      selectedTimeSeries(value) {
        this.loading = true
        this.hasError = false
        this.errorMessage = null
        if (!value) return

        try {
          let args = this.constructDefaultGraphingArguments(value)
          let twoWeeksAgo = moment().subtract(14, 'days')

          if (args.timeRange[1] < twoWeeksAgo) {
            this.hasError = true
            this.loading = false
            this.errorMessage = 'No data available for ' + value.DisplayName +
              ' in the past two weeks.'
          } else {
            this.fetchDataFromGraph(args.datasets, args.timeRange[0], args.timeRange[1]).then(seriesData => {
              this.drawChartWithArgs(args, seriesData, null)
              for (let item of document.getElementsByClassName('highcharts-autoscale-button')) {
                item.dispatchEvent(new CustomEvent('click'))
              }
              this.loading = false
            })
          }
        } catch (e) {
          this.hasError = true
          this.loading = false
        }
      },
    },
    methods: {
      generate() {
        this.loading = true
        this.hasError = false

        if (!this.datasets) {
          this.hasError = true
          this.loading = false
          return
        }

        try {
          let args = this.constructGraphingArguments()

          if (!args) {
            this.hasError = true
            this.loading = false
            return
          }

          let hasHistorical = this.sortedDatasets.some(x => x.DataSetIdentifer.indexOf(HISTORIC_DISCHARGE) >= 0)
          this.fetchDataFromGraph(args.datasets, args.timeRange[0], args.timeRange[1]).then(seriesData => {
            this.drawChartWithArgs(args, seriesData, hasHistorical ? {
              shared: true,
              valueSuffix: ` ${CUBIC_METRE_PER_SECOND}`,
            } : null)
            this.loading = false
          })
        } catch (error) {
          console.error(error)
          this.hasError = true
          this.loading = false
        }
      },
      constructDefaultGraphingArguments(selectedTimeSeries) {
        let serieData = selectedTimeSeries
        if (serieData == null) {
          serieData = this.datasets[0]
        }

        let config = this.constructArgumentForSingleDataset(serieData, 0, true)

        let graphingArguments = {
          title: GRAPH_TITLE,
          timeRange: config.timeRange,
          datasets: [config.dataset],
          yAxis: [config.yAxis],
        }
        return graphingArguments
      },
      constructArgumentForSingleDataset(dataset, index, oppositeYAxis) {
        let timeSeriesColor = Highcharts.getOptions().colors[index % 10]
        let timeRange = this.decideTimeRange(dataset)
        let dataSetConfig = {
          name: dataset.DataSetIdentifer,
          type: this.decideChartType(dataset.DataSetIdentifer),
          yAxis: index,
          color: timeSeriesColor,
        }
        let yAxisConfig = { // Primary yAxis
          labels: {
            format: '',
            style: {
              color: timeSeriesColor,
            },
          },
          title: {
            text: dataset.DataSetIdentifer,
            style: {
              color: timeSeriesColor,
            },
          },
          opposite: oppositeYAxis,
        }

        return {
          timeRange: timeRange,
          dataset: dataSetConfig,
          yAxis: yAxisConfig,
        }
      },
      constructGraphingArguments() {
        let targetDatasetParts = relevantDatasets[this.locationType]
        if (!targetDatasetParts) return this.constructDefaultGraphingArguments()

        let yAxisIndex = -1
        let datasets = []
        let yAxis = []
        let timeRanges = []
        let opposite = true

        let targetDatasets = targetDatasetParts.flat()

        targetDatasets.forEach(targetDataset => {
          let matchedDataset = this.sortedDatasets.find(sortedDataset => (
            sortedDataset.DataSetIdentifer.includes(targetDataset)
          ))

          if (matchedDataset == null) return

          yAxisIndex++
          let config = this.constructArgumentForSingleDataset(matchedDataset, yAxisIndex, opposite)
          timeRanges.push(config.timeRange)
          datasets.push(config.dataset)
          yAxis.push(config.yAxis)
          opposite = !opposite
        })

        if (yAxisIndex > -1) {
          // find the latest time range
          let tt = timeRanges.map(x => x[1].valueOf())
          let indexOfTimeRange = tt.indexOf(Math.max.apply(Math, tt))
          let latestTimeRange = timeRanges[indexOfTimeRange]
          let graphingArguments = {
            title: GRAPH_TITLE,
            timeRange: latestTimeRange,
            datasets: datasets,
            yAxis: yAxis,
          }
          return graphingArguments
        }
      },

      decideTimeRange(timeSeries) {
        let endDate = moment(timeSeries.LastRecordDate)
        let startDate = moment(timeSeries.LastRecordDate).subtract(14, 'days')
        return [startDate, endDate]
      },
      async fetchDataFromGraph(datasetsArgs, startDate, endDate) {
        let hasHistoricalData = false
        let data = []
        let datasetnames = datasetsArgs.map(x => x.name)
        let matchedDataset = this.sortedDatasets.filter(x => datasetnames.indexOf(x.DataSetIdentifer) >= 0)

        //if matched dataset contain discharge data, try to find historical data time series
        let dischargeTimeSeries = matchedDataset.filter(x => x.DataSetIdentifer.indexOf('Discharge') >= 0)
        if (dischargeTimeSeries.length > 0) {
          let foundHistoricalTimeSeries = this.sortedDatasets.filter(x => x.DataSetIdentifer.indexOf(
            HISTORIC_DISCHARGE) >= 0)
          if (foundHistoricalTimeSeries.length > 0) {
            hasHistoricalData = true
            matchedDataset = matchedDataset.concat(foundHistoricalTimeSeries)
          }
        }

        let url = 'api/GraphAPI?startDate=' + startDate.format('YYYY-MM-DD') +
          '&endDate=' + endDate.format('YYYY-MM-DD') +
          '&includeTime=false&tryCompression=true&includeGapMarkers=false'
        await axios.post(url, matchedDataset).then((res) => {
          let responseData = res.data
          let historicalData = []
          let masterConfig = null
          if (hasHistoricalData) {
            historicalData = responseData.filter(x => x.DataSetName.indexOf(HISTORIC_DISCHARGE) >= 0)
            masterConfig = datasetsArgs.find(x => x.name.indexOf('Discharge') >= 0)
            responseData = this.removeElementsFromArray(responseData, historicalData)
          }
          for (let i = 0; i < responseData.length; i++) {
            let dataForSeries = responseData[i]
            if (dataForSeries.DataSetName.indexOf(HISTORIC_DISCHARGE) >= 0) {
              continue
            }
            let rows = dataForSeries.Rows.map(x => [moment(x.DateTime).valueOf(), x.Value])
            let approvalLevels = dataForSeries.Rows.map(x => x.ApprovalLevel)
            var dataGrades = dataForSeries.Rows.map(x => x.Grade)
            var dataQualifiers = dataForSeries.Rows.map(x => x.Qualifier)
            data.push({
              name: dataForSeries.DataSetName,
              data: rows,
              dataQaQc: approvalLevels,
              dataGrades: dataGrades,
              dataQualifiers: dataQualifiers,
              yAxis: datasetsArgs[i].yAxis,
              type: datasetsArgs[i].type,
              unit: dataForSeries.Unit,
              color: datasetsArgs[i].color,
            })
          }
          if (hasHistoricalData) {
            let rangeTimeSeries = this.constructAreaChart(historicalData, masterConfig)
            data = data.concat(rangeTimeSeries)
          }

        })
        return data
      },

    },
  }
</script>